import React, { useRef } from 'react';
import { Button } from 'baseui/button';
import { Checkbox, LABEL_PLACEMENT } from 'baseui/checkbox';
import { Input } from 'baseui/input';
import { Textarea } from 'baseui/textarea';
import { PlayIcon } from '../../styles/iconStyles';
import { soundConfig } from '../../constants/display/soundConfig';
import {
  ModalContentWrapper,
  SettingsSectionContainer,
  SettingsRow,
  SettingsLabel,
  SettingsInputGroup,
  StretchedSettingsInputGroup,
  StretchedInputOverrides,
  MaxDurationInputOverrides,
  createDarkThemeCheckboxOverrides,
  iconSizes,
  SpeechTextPlaceholderContainer,
  ButtonWrapContainer,
} from '../../styles/displaySettingsModalStyles';

const SoundSettingsTab = ({
  category,
  settings,
  actions,
  t,
  palette,
  structure,
  setSoundSelectionContext,
  setIsSoundSelectionModalOpen,
  setSelectedSoundFileNameForModal,
  isTestingSound,
  onTestSound
}) => {
  const config = soundConfig[category];
  const textareaRef = useRef(null);
  const [isSoundMissing, setIsSoundMissing] = React.useState(false);

  const { soundId } = config || {};
  const { enableSound, selectedSound, maxDuration, enableSpeech, speechText } = settings || {};
  const { handleSettingChange, speak, getAudioContext, checkSoundExists } = actions;

  React.useEffect(() => {
    const verifySound = async () => {
      if (enableSound && selectedSound && checkSoundExists) {
        const exists = await checkSoundExists(soundId);
        setIsSoundMissing(!exists);
      } else {
        setIsSoundMissing(false);
      }
    };
    verifySound();
  }, [enableSound, selectedSound, soundId, checkSoundExists]);

  if (!config || !settings) return null;

  const testSpeech = () => {
    if (enableSpeech && speechText && speechText.trim()) {
      // 最初に見つかったレベル（休憩以外）のデータを使用してプレースホルダーを展開する
      const firstLevel = structure?.levels?.find(row => row.type === 'level');
      speak(speechText, firstLevel, structure?.columns, true);
    }
  };

  const handleInsertPlaceholder = (placeholder) => {
    const el = textareaRef.current;
    const textToInsert = `[${placeholder}]`;
    let newText = (speechText || '');

    if (el) {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const before = newText.substring(0, start);
      const after = newText.substring(end);
      newText = before + textToInsert + after;

      handleSettingChange(category, 'speechText', newText);

      // フォーカスを戻してカーソル位置を調整
      setTimeout(() => {
        el.focus();
        const newPos = start + textToInsert.length;
        el.setSelectionRange(newPos, newPos);
      }, 0);
    } else {
      newText += textToInsert;
      handleSettingChange(category, 'speechText', newText);
    }
  };

  return (
    <ModalContentWrapper>
      <SettingsSectionContainer>
        <SettingsRow>
          <Checkbox
            checked={!!enableSound}
            onChange={e => handleSettingChange(category, 'enableSound', e.target.checked)}
            labelPlacement={LABEL_PLACEMENT.right}
            overrides={createDarkThemeCheckboxOverrides(palette)}
          >
            {t('display.enableSoundEffectLabel')}
          </Checkbox>
        </SettingsRow>
        {enableSound && (
          <>
            <SettingsRow>
              <SettingsInputGroup>
                <Button size="compact" onClick={() => { setSoundSelectionContext(category); setSelectedSoundFileNameForModal(selectedSound || ''); setIsSoundSelectionModalOpen(true); getAudioContext(); }}>
                  {selectedSound || t('display.selectSoundEffectButtonLabel')}
                </Button>
                <Button size="compact" shape="circle" title={t('display.testSoundEffectButtonTooltip')} onClick={() => onTestSound(soundId, maxDuration)} disabled={isTestingSound}>
                  <PlayIcon $size={iconSizes.ACTION} />
                </Button>
              </SettingsInputGroup>
              {isSoundMissing && (
                <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                  ⚠️ {t('display.soundFileMissingWarning') || '音声ファイルが見つかりません。再設定してください。'}
                </div>
              )}
            </SettingsRow>
            <SettingsRow>
              <SettingsLabel htmlFor={`${category}-maxDuration`}>{t('display.maxDurationLabel')}</SettingsLabel>
              <Input id={`${category}-maxDuration`} type="number" value={maxDuration || 1} onChange={e => handleSettingChange(category, 'maxDuration', parseInt(e.target.value, 10) || 1)} size="compact" overrides={MaxDurationInputOverrides} />
            </SettingsRow>
          </>
        )}
      </SettingsSectionContainer>
      {category === 'levelEndWarning' && (
        <SettingsSectionContainer>
          <SettingsRow>
            <SettingsLabel htmlFor={`${category}-seconds`}>{t('display.secondsBeforeEndLabel')}</SettingsLabel>
            <Input id={`${category}-seconds`} type="number" value={settings.secondsBeforeEnd || 1} onChange={e => handleSettingChange(category, 'secondsBeforeEnd', Math.max(1, parseInt(e.target.value, 10) || 1))} size="compact" overrides={MaxDurationInputOverrides} />
          </SettingsRow>
        </SettingsSectionContainer>
      )}
      <SettingsSectionContainer>
        <SettingsRow>
          <Checkbox
            checked={!!enableSpeech}
            onChange={e => handleSettingChange(category, 'enableSpeech', e.target.checked)}
            labelPlacement={LABEL_PLACEMENT.right}
            overrides={createDarkThemeCheckboxOverrides(palette)}
          >
            {t('display.enableSpeechLabel')}
          </Checkbox>
        </SettingsRow>
        {enableSpeech && (
          <>
            <SettingsRow>
              <StretchedSettingsInputGroup>
                <Textarea
                  value={speechText || ''}
                  onChange={e => handleSettingChange(category, 'speechText', e.target.value)}
                  placeholder={t('display.speechTextPlaceholder')}
                  overrides={StretchedInputOverrides}
                  rows={3}
                  inputRef={textareaRef}
                />
                <Button size="compact" shape="circle" title={t('display.testSpeechButtonTooltip')} onClick={testSpeech}>
                  <PlayIcon $size={iconSizes.ACTION} />
                </Button>
              </StretchedSettingsInputGroup>
            </SettingsRow>
            {category === 'levelStart' && structure?.columns && (
              <SettingsRow>
                <SpeechTextPlaceholderContainer>
                  <SettingsLabel>{t('display.availablePlaceholders')}</SettingsLabel>
                  <ButtonWrapContainer>
                    {structure.columns.map(col => (
                      <Button key={col.id} size="mini" onClick={() => handleInsertPlaceholder(col.label)}>[{col.label}]</Button>
                    ))}
                  </ButtonWrapContainer>
                </SpeechTextPlaceholderContainer>
              </SettingsRow>
            )}
          </>
        )}
      </SettingsSectionContainer>
    </ModalContentWrapper>
  );
};

export default SoundSettingsTab;
